import { coreClient, apiRequest } from '../client.js';
import type {
  BotName,
  JsonValue,
  LLMGenerateParams,
  LLMResponse,
  LLMProvider,
  RoutingDecision,
  BotConfiguration,
  ProviderModelsResponse,
  OpenRouterProvider,
  ModelEndpoint,
} from '../types.js';

type RawOpenRouterProvider = Omit<OpenRouterProvider, 'code'> & { slug: string };
type RawModelEndpoint = Omit<ModelEndpoint, 'providerCode' | 'uptime'> & { tag: string; uptime_last_30m: number };

export const llmApi = {
  generate: (params: LLMGenerateParams) =>
    apiRequest<LLMResponse>(coreClient, {
      method: 'POST',
      url: '/llm/generate',
      data: params,
    }),

  getRoute: (params: {
    botName: BotName;
    isLewd?: boolean;
    requiresVision?: boolean;
  }) =>
    apiRequest<RoutingDecision>(coreClient, {
      method: 'POST',
      url: '/llm/route',
      data: params,
    }),

  getProvidersHealth: () =>
    apiRequest<{ providers: Record<string, LLMProvider> }>(coreClient, {
      method: 'GET',
      url: '/llm/providers/health',
    }),

  recoverProvider: (provider: string) =>
    apiRequest<{ message: string; health: Record<string, unknown> }>(coreClient, {
      method: 'POST',
      url: `/llm/providers/${provider}/recover`,
    }),

  getBotConfig: (botName: BotName) =>
    apiRequest<{ configurations: BotConfiguration[] }>(coreClient, {
      method: 'GET',
      url: `/llm/bots/${botName}/config`,
    }),

  getBotConfigById: (botName: BotName, configId: string) =>
    apiRequest<{ configuration: BotConfiguration }>(coreClient, {
      method: 'GET',
      url: `/llm/bots/${botName}/config/${configId}`,
    }),

  updateBotConfig: (botName: BotName, configId: string, config: Partial<BotConfiguration>) =>
    apiRequest<{ configuration: BotConfiguration }>(coreClient, {
      method: 'PUT',
      url: `/llm/bots/${botName}/config/${configId}`,
      data: config,
    }),

  createBotConfig: (botName: BotName, config: Partial<BotConfiguration>) =>
    apiRequest<{ configuration: BotConfiguration }>(coreClient, {
      method: 'POST',
      url: `/llm/bots/${botName}/config`,
      data: config,
    }),

  deleteBotConfig: (botName: BotName, configId: string) =>
    apiRequest<{ message: string }>(coreClient, {
      method: 'DELETE',
      url: `/llm/bots/${botName}/config/${configId}`,
    }),

  getBotPrompts: (botName: BotName) =>
    apiRequest<BotConfiguration[]>(coreClient, {
      method: 'GET',
      url: `/llm/bots/${botName}/prompts`,
    }),

  saveBotPrompt: (botName: BotName, prompt: Partial<BotConfiguration>) =>
    apiRequest<{ configuration: BotConfiguration }>(coreClient, {
      method: 'POST',
      url: `/llm/bots/${botName}/config`,
      data: prompt,
    }),

  getConfigVersions: (botName: BotName, configId: string) =>
    apiRequest<Array<Record<string, JsonValue>>>(coreClient, {
      method: 'GET',
      url: `/llm/bots/${botName}/config/${configId}/versions`,
    }),

  getProviderModels: () =>
    apiRequest<ProviderModelsResponse>(coreClient, {
      method: 'GET',
      url: '/llm/providers/models',
    }),

  refreshProviderModels: () =>
    apiRequest<{ message: string; data: ProviderModelsResponse }>(coreClient, {
      method: 'POST',
      url: '/llm/providers/models/refresh',
    }),

  refreshOllamaModels: () =>
    apiRequest<{ message: string; data: { ollama: Record<string, unknown>; lastUpdated: string } }>(coreClient, {
      method: 'POST',
      url: '/llm/providers/models/refresh-ollama',
    }),

  getOpenRouterProviders: async () => {
    const response = await apiRequest<{ data: RawOpenRouterProvider[] }>(coreClient, {
      method: 'GET',
      url: '/llm/openrouter/providers',
    });
    return {
      providers: (response.data || []).map(p => ({
        ...p,
        code: p.slug,
      })),
    };
  },

  getModelEndpoints: async (modelId: string) => {
    const response = await apiRequest<{ data: RawModelEndpoint[] }>(coreClient, {
      method: 'GET',
      url: `/llm/openrouter/models/${modelId.replace('/', ':')}/endpoints`,
    });
    return {
      endpoints: (response.data || []).map(e => ({
        ...e,
        providerCode: e.tag,
        uptime: e.uptime_last_30m,
      })) || [],
    };
  },

  getOllamaModels: () =>
    apiRequest<{ data: Array<{ id: string; name: string } & Record<string, unknown>> }>(coreClient, {
      method: 'GET',
      url: '/llm/ollama/models',
    }),
};

export default llmApi;
